import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import keycloak, {
  initKeycloak,
  getUserInfo,
  refreshToken,
  login as keycloakLogin,
  logout as keycloakLogout,
  persistTokens,
} from './keycloak'
import { authApi } from '../services/api'
import {
  clearLocalSession,
  getLocalAccessToken,
  getLocalSession,
  saveLocalSession,
} from './localAuth'
import {
  SESSION_EXPIRED_EVENT,
  clearKeycloakTokens,
  clearSessionEnded,
  consumeSessionEnded,
  type SessionEndReason,
} from './sessionStore'

export interface User {
  id: string
  username: string
  email?: string
  firstName?: string
  lastName?: string
  institutionId?: string
  roles: string[]
  authProvider: 'local' | 'keycloak'
  mustChangePassword?: boolean
}

interface AuthContextType {
  initialized: boolean
  isAuthenticated: boolean
  user: User | null
  roles: string[]
  token: string | undefined
  authProvider: 'local' | 'keycloak' | null
  login: () => void
  loginLocal: (username: string, password: string) => Promise<{ ok: boolean; error?: string }>
  logout: () => void
  hasRole: (role: string) => boolean
  /**
   * Why the previous session ended, if it ended by itself (FE issue #24).
   * `null` for a fresh visit or an explicit sign-out — the login page only
   * explains something that actually happened.
   */
  sessionEndedReason: SessionEndReason | null
  dismissSessionNotice: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: React.ReactNode
}

const normalizeLocalUser = (user: {
  id: string
  username: string
  email?: string
  first_name?: string
  last_name?: string
  roles: string[]
  must_change_password?: boolean
}): User => ({
  id: user.id,
  username: user.username,
  email: user.email,
  firstName: user.first_name,
  lastName: user.last_name,
  roles: user.roles || [],
  authProvider: 'local',
  mustChangePassword: user.must_change_password || false,
})

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [initialized, setInitialized] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState<User | null>(null)
  const [authProvider, setAuthProvider] = useState<'local' | 'keycloak' | null>(null)
  // Read once, on the first render after the page load that followed the
  // expiry — the flag is consumed so the message is shown exactly once.
  const [sessionEndedReason, setSessionEndedReason] = useState<SessionEndReason | null>(
    () => consumeSessionEnded(),
  )

  const dismissSessionNotice = useCallback(() => {
    clearSessionEnded()
    setSessionEndedReason(null)
  }, [])

  // A 401 on any API call (expired local token, revoked Keycloak session) now
  // raises this event instead of hard-redirecting to '/', which used to drop
  // the user on the login screen with no explanation at all.
  useEffect(() => {
    const onExpired = (event: Event) => {
      const reason = (event as CustomEvent<SessionEndReason>).detail || 'expired'
      clearLocalSession()
      clearKeycloakTokens()
      setIsAuthenticated(false)
      setUser(null)
      setAuthProvider(null)
      setSessionEndedReason(reason)
    }
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired)
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired)
  }, [])

  useEffect(() => {
    const localSession = getLocalSession()
    if (localSession) {
      setIsAuthenticated(true)
      setUser(localSession.user)
      setAuthProvider('local')
      setInitialized(true)
      return
    }

    initKeycloak(() => {
      if (keycloak.authenticated) {
        const info = getUserInfo()
        if (info) {
          setUser({
            ...info,
            roles: info.roles || [],
            authProvider: 'keycloak',
          } as User)
          setIsAuthenticated(true)
          setAuthProvider('keycloak')
        }
      }
      setInitialized(true)
    })

    const refreshInterval = setInterval(() => {
      if (keycloak.authenticated) {
        refreshToken(60)
      }
    }, 60000)

    return () => {
      clearInterval(refreshInterval)
    }
  }, [])

  useEffect(() => {
    keycloak.onTokenExpired = () => {
      refreshToken(0)
    }

    keycloak.onAuthSuccess = () => {
      const info = getUserInfo()
      // Keep the per-tab restore copy fresh so F5 keeps the session (#24).
      persistTokens()
      setSessionEndedReason(null)
      setIsAuthenticated(true)
      setUser(
        info
          ? ({
              ...info,
              roles: info.roles || [],
              authProvider: 'keycloak',
            } as User)
          : null
      )
      setAuthProvider('keycloak')
    }

    keycloak.onAuthRefreshSuccess = () => {
      persistTokens()
    }

    keycloak.onAuthError = () => {
      clearKeycloakTokens()
      if (authProvider !== 'local') {
        setIsAuthenticated(false)
        setUser(null)
        setAuthProvider(null)
      }
    }

    keycloak.onAuthLogout = () => {
      clearKeycloakTokens()
      if (authProvider !== 'local') {
        setIsAuthenticated(false)
        setUser(null)
        setAuthProvider(null)
      }
    }
  }, [authProvider])

  const hasRole = useCallback((role: string): boolean => {
    return user?.roles.includes(role) || false
  }, [user])

  const login = useCallback(() => {
    keycloakLogin()
  }, [])

  const loginLocal = useCallback(
    async (username: string, password: string): Promise<{ ok: boolean; error?: string }> => {
      try {
        const response = await authApi.localLogin(username, password)
        const data = response.data
        const mappedUser = normalizeLocalUser(data.user)

        saveLocalSession(data.access_token, data.expires_in, mappedUser)
        clearSessionEnded()
        setSessionEndedReason(null)
        setIsAuthenticated(true)
        setUser(mappedUser)
        setAuthProvider('local')

        return { ok: true }
      } catch (error: any) {
        const detail = error?.response?.data?.detail
        const status = error?.response?.status
        const fallback =
          status === 404
            ? 'Local login endpoint is unreachable. Verify API proxy target (VITE_API_PROXY_TARGET) and backend URL.'
            : 'Local login failed. Check username and password.'
        if (detail) {
          return { ok: false, error: detail }
        }
        return { ok: false, error: fallback }
      }
    },
    []
  )

  const logout = useCallback(() => {
    // An explicit sign-out must not show the "session expired" notice.
    clearSessionEnded()
    setSessionEndedReason(null)
    if (authProvider === 'local') {
      clearLocalSession()
      setIsAuthenticated(false)
      setUser(null)
      setAuthProvider(null)
      return
    }

    setIsAuthenticated(false)
    setUser(null)
    setAuthProvider(null)
    keycloakLogout()
  }, [authProvider])

  const contextValue: AuthContextType = {
    initialized,
    isAuthenticated,
    user,
    roles: user?.roles || [],
    token: authProvider === 'local' ? getLocalAccessToken() || undefined : keycloak.token,
    authProvider,
    login,
    loginLocal,
    logout,
    hasRole,
    sessionEndedReason,
    dismissSessionNotice,
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export default AuthProvider
